import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, ViewChild, ViewRef, computed, effect, inject, signal, untracked, viewChild, viewChildren } from '@angular/core';
import { SelectedAttributeService } from '../../services/selected-attribute.service';
import { Material } from 'src/app/interfaces/material';
import { assert } from 'src/app/utils/assert.util';
import { UnavailableDialogComponent } from '../unavailable.dialog/unavailable.dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MaterialsService } from 'src/app/services/materials/materials.service';
import { ConfiguredItemService } from 'src/app/services/configured-item/configured-item.service';
import { RulesService } from 'src/app/services/rules/rules.service';
import { WidthsService } from 'src/app/services/widths/widths.service';
import { getAssetURL } from 'src/app/constants/assets';
import { LoadingService } from 'src/app/services/loading/loading.service';
import { GoogleAnalyticsService } from 'src/app/services/google-analytics/google-analytics.service';
import { Subscription } from 'rxjs';
import { CheckoutService } from '../../services/checkout.service';
import { Navigation2Service } from '../../services/navigation2.service';
import { ScrollService } from 'src/app/services/scroll/scroll.service';

@Component({
  selector: 'app-material-select',
  templateUrl: './material-select.component.html',
  styleUrls: ['./material-select.component.scss']
})
export class MaterialSelectComponent {
  private scrollService = inject(ScrollService);
  private navigationService = inject(Navigation2Service);
  private checkoutService = inject(CheckoutService);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private materialsService = inject(MaterialsService);
  private selectedAttributeService = inject(SelectedAttributeService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);
  private rulesService = inject(RulesService);
  private loadingService = inject(LoadingService);
  private materialCards = viewChildren('MaterialCard', { read: ElementRef<HTMLDivElement> });
  private container = viewChild('Container', { read: ElementRef });
  private undoRedoChangeSubscription: Subscription | null = null;
  @Input() type!: 'Primary' | 'Secondary';
  @Input() shouldAnimate = false;
  @Output() onInit = new EventEmitter<number>();
  @ViewChild('materialContainer', {static: true}) materialContainer!: ElementRef<HTMLDivElement>;
  materials: Material[] = [];
  selectedMaterial: Material | null = null;
  shouldAnimateSelection = false;
  shouldScroll = signal(true);
  // availableMaterials = computed(() => this.type === 'Primary' ? this.selectedAttributeService.availableAttributes().primaryMaterial : this.selectedAttributeService.availableAttributes().secondaryMaterial);
  availableMaterials = signal<number[]>([]);
  reachableMaterials = signal<number[]>([]);
  canSetupAvailableMaterials = signal(false);
  filteredMaterials = signal<Material[]>([]);
  materialsToShow = computed(() => {
    const isShowingUnavailable = this.selectedAttributeService.shouldShowUnavailable();
    const reachableMaterials = this.reachableMaterials();
    const filteredMaterials = this.filteredMaterials();
    const materialsToUse = filteredMaterials.length > 0 ? filteredMaterials : this.materials;
    if (isShowingUnavailable) {
      return materialsToUse;
    }
    const configuredItem = this.selectedAttributeService.getValue();
    const selectedMaterial = this.type === 'Primary' ? configuredItem.primaryMaterial : configuredItem.secondaryMaterial;
    return materialsToUse.filter(m => reachableMaterials.includes(m.id) || m.id === selectedMaterial?.id);
  });

  constructor() {
    effect(() => {
      if (this.canSetupAvailableMaterials()) {
        const configuredItem = this.selectedAttributeService.getValue();
        if (this.type === 'Primary') {
          this.selectedMaterial = configuredItem.primaryMaterial;
        } else {
          this.selectedMaterial = configuredItem.secondaryMaterial;
        }
      }
    });

    effect(async () => {
      this.loadingService.load('reachableMaterials');
      const iframeID = untracked(() => this.checkoutService.isRetailerOnly()) ? this.checkoutService.retailerIframeID : null;
      const reachableAttributes = await (this.type === 'Primary' ? this.rulesService.getReachablePrimaryMaterials(this.selectedAttributeService.configuredItem(), iframeID) : this.rulesService.getReachableSecondaryMaterials(this.selectedAttributeService.configuredItem(), iframeID));
      this.reachableMaterials.set(reachableAttributes);
      this.loadingService.done('reachableMaterials');
    }, {allowSignalWrites: true});

    effect(async () => {
      this.loadingService.load('materials');
      const availableAttributes = await this.selectedAttributeService.availableAttributes();
      this.availableMaterials.set(this.type === 'Primary' ? availableAttributes.insideMaterialIDs : availableAttributes.outsideMaterialIDs);
      this.loadingService.done('materials');
    }, {allowSignalWrites: true});

    effect(() => {
      this.navigationService.subtitle.set(this.type === 'Primary' ? (this.selectedAttributeService.configuredItem().primaryMaterial?.displayName ?? null) : (this.selectedAttributeService.configuredItem().secondaryMaterial?.displayName ?? null));
    }, { allowSignalWrites: true });

    effect(async () => {
      const cards = this.materialCards();
      const a = await this.reachableMaterials()
      const container = this.container();
      if (untracked(() => this.shouldScroll()) && container) {
        this.scrollService.scrollToPosition(`material-${this.type}`, container.nativeElement);
        this.shouldScroll.set(a.length === 0 || a.length !== cards.length);
      }
    }, { allowSignalWrites: true });
  }

  ngOnInit() {
    if (this.googleAnalyticsService.isInitialized) {
      this.googleAnalyticsService.triggerEvent('view', 'attribute_panel', 'section_view', `${this.type.toLowerCase()}_material`);
    }

    this.materials = this.materialsService.values().filter(m => m.type === this.type);
    // this.materialsToShow = this.getMaterialsToShow();
    this.canSetupAvailableMaterials.set(true);
    if (this.type === 'Primary') {
      this.selectedMaterial = this.selectedAttributeService.configuredItem().primaryMaterial;
    } else {
      this.selectedMaterial = this.selectedAttributeService.configuredItem().secondaryMaterial;
    }
    this.changeDetectorRef.detectChanges();
    this.onInit.emit(this.materialContainer.nativeElement.offsetHeight);
  }

  // getMaterialsToShow() {
  //   const isShowingUnavailable = this.selectedAttributeService.shouldShowUnavailable();
  //   if (isShowingUnavailable) {
  //     this.materials.sort((a, b) => (this.availableMaterials().includes(b.id) ? 1 : 0) - (this.availableMaterials().includes(a.id) ? 1 : 0));
  //     return this.materials;
  //   }
  //   const configuredItem = this.selectedAttributeService.getValue();
  //   const selectedMaterial = this.type === 'Primary' ? configuredItem.primaryMaterial : configuredItem.secondaryMaterial;
  //   return this.materials.filter(m => this.availableMaterials().includes(m.id) || m.id === selectedMaterial?.id);
  // }

  ngAfterViewInit() {
    this.shouldAnimateSelection = true;
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy() {
    this.undoRedoChangeSubscription?.unsubscribe();

    const container = this.container();
    if (container) {
      this.scrollService.savePosition(`material-${this.type}`, container.nativeElement.scrollTop);
    }
  }

  async selectMaterial(material: Material) {
    try {
      if (this.googleAnalyticsService.isInitialized) {
        this.googleAnalyticsService.triggerEvent('click', 'attribute_panel', `${material.type.toLowerCase()}_material`, material.shortName);
      }

      if (this.availableMaterials().includes(material.id)) {
        if (this.type === 'Primary') {
          await this.selectedAttributeService.mutatePrimaryMaterial(material);
        } else {
          await this.selectedAttributeService.mutateSecondaryMaterial(material);
        }
      } else {
        if (this.type === 'Primary') {
          this.loadingService.load('SetClosestByPrimaryMaterial');
          await this.selectedAttributeService.setClosestByPrimaryMaterial(material);
        } else {
          this.loadingService.load('SetClosestBySecondaryMaterial');
          await this.selectedAttributeService.setClosestBySecondaryMaterial(material);
        }
      }
    } catch(e) {
      this.dialog.open(UnavailableDialogComponent, { maxWidth: 450, data: 'material' });
    } finally {
      if (this.type === 'Primary') {
        this.loadingService.done('SetClosestByPrimaryMaterial');
      } else {
        this.loadingService.done('SetClosestBySecondaryMaterial');
      }
    }
  }

  getFullMaterialURL(material: Material) {
    if (material.thumbnail) {
      return getAssetURL(material.thumbnail);
    }
    return null;
  }

  valueExtractor(material: Material) {
    return material.displayName;
  }

  filterChange(filteredMaterials: any) {
    this.filteredMaterials.set(filteredMaterials);
  }
}
